import moment from 'moment-timezone';
import { getDurationInDays, getDurationInMinutes, getMomentDate, getUtcOffset, now } from './date';
export const getDay = (date, timeZone, locale) => formatWithIntl({
    timeZone,
    day: '2-digit',
}, date, locale);
export const getWeekDay = (date, timeZone, locale) => formatWithIntl({
    timeZone,
    weekday: 'short',
}, date, locale);
export const getMonth = (date, timeZone, locale) => formatWithIntl({
    timeZone,
    month: 'short',
}, date, locale);
export const getYear = (date, timeZone, locale) => formatWithIntl({
    timeZone,
    year: 'numeric',
}, date, locale);
export const getFullDate = (timestamp, timeZone, showYear, locale, showTimeZone = false) => formatWithIntl({
    timeZone,
    year: showYear ? 'numeric' : undefined,
    month: 'short',
    day: '2-digit',
    hour: 'numeric',
    minute: 'numeric',
    timeZoneName: showTimeZone ? 'short' : undefined,
}, timestamp, locale);
export const getLongDate = (timestamp, timeZone, showYear, locale) => formatWithIntl({
    timeZone,
    month: 'short',
    day: '2-digit',
    hour: 'numeric',
    minute: 'numeric',
    year: showYear ? 'numeric' : undefined,
}, timestamp, locale);
export const getShortDate = (timestamp, timeZone, locale) => formatWithIntl({
    timeZone,
    weekday: 'short',
    month: 'short',
    day: '2-digit',
}, timestamp, locale);
export const getShortDateTime = (timestamp, timeZone, locale, showTimeZone = false) => formatWithIntl({
    timeZone,
    weekday: 'short',
    month: 'short',
    day: '2-digit',
    hour: 'numeric',
    minute: '2-digit',
    timeZoneName: showTimeZone ? 'short' : undefined,
}, timestamp, locale);
export const getYearMonthDay = (timestamp, timeZone, locale) => formatWithIntl({
    timeZone,
    year: 'numeric',
    month: 'short',
    day: '2-digit',
}, timestamp, locale);
export const getYearMonth = (timestamp, timeZone, locale) => formatWithIntl({
    timeZone,
    year: 'numeric',
    month: 'short',
}, timestamp, locale);
export const getMonthDay = (timestamp, timeZone, locale) => formatWithIntl({
    timeZone,
    month: 'long',
    day: 'numeric',
}, timestamp, locale);
export const getTime = (timestamp, timeZone, locale, showTimeZone = false) => formatWithIntl({
    timeZone,
    minute: '2-digit',
    hour: 'numeric',
    timeZoneName: showTimeZone ? 'short' : undefined,
}, timestamp, locale);
export const getWeekday = (timestamp, locale) => formatWithIntl({
    weekday: 'long',
}, timestamp, locale);
export const getShortWeekday = (timestamp, locale) => formatWithIntl({
    weekday: 'short',
}, timestamp, locale);
export const getNarrowWeekday = (timestamp, locale) => formatWithIntl({
    weekday: 'narrow',
}, timestamp, locale);
export const formatDates = (startDate, endDate, timeZone, forceShowYear = false, locale = 'en-US', showTimeZone = false) => {
    const start = getMomentDate(startDate, timeZone);
    const end = endDate ? getMomentDate(endDate, timeZone) : null;
    const showYear = !isYearHidden(start, end) || forceShowYear;
    if (endDate) {
        const sameDay = start.isSame(end, 'day');
        const showStartDateTimeZone = showTimeZone && !sameDay;
        const formattedStart = getFullDate(startDate, timeZone, showYear, locale, showStartDateTimeZone);
        if (sameDay) {
            return `${formattedStart} – ${getTime(endDate, timeZone, locale, showTimeZone)}`;
        }
        else {
            return `${formattedStart} – ${getFullDate(endDate, timeZone, showYear, locale, showTimeZone)}`;
        }
    }
    return `${getFullDate(startDate, timeZone, showYear, locale, showTimeZone)}`;
};
export const isYearHidden = (start, end) => {
    const current = moment();
    return current.isSame(start, 'year') && current.isSame(end || moment(), 'year');
};
// The wall time comes from moment-timezone, because browsers can ship older tzdata than it does
export const formatWithIntl = (options, timestamp, locale) => {
    if (options.timeZone === undefined) {
        try {
            return new Intl.DateTimeFormat(locale, options).format(new Date(timestamp));
        }
        catch {
            // An invalid date (an event with no date and no zone) still gets the UTC output below
        }
    }
    try {
        const offset = getUtcOffset(timestamp, options.timeZone);
        const wallTime = new Date(getMomentDate(timestamp).add(offset, 'minutes').toISOString());
        const utcFormat = new Intl.DateTimeFormat(locale, { ...options, timeZone: 'UTC' });
        const formatted = utcFormat.format(wallTime);
        if (!options.timeZoneName) {
            return formatted;
        }
        try {
            const label = getTimeZoneName(new Intl.DateTimeFormat(locale, options), new Date(timestamp));
            return formatted.replace(getTimeZoneName(utcFormat, wallTime), label);
        }
        catch {
            // Intl rejects a zone its tzdata lacks, so the label stays UTC
            return formatted;
        }
    }
    catch {
        return moment(timestamp).tz(options.timeZone).format('LLL');
    }
};
const getTimeZoneName = (format, date) => format.formatToParts(date).find(part => part.type === 'timeZoneName').value;
export const formatTimeZone = (locale, timezone) => new Intl.DateTimeFormat(locale, {
    timeZoneName: 'short',
    timeZone: timezone,
})
    .formatToParts(new Date())
    .find(part => part.type === 'timeZoneName').value;
export const formatDurationUnderHour = (duration) => moment.utc(duration).format('mm[:]ss');
export const getFormattedEventDuration = ({ startTimestamp, endTimestamp, locale, timeZoneId, }) => {
    const start = moment(startTimestamp).startOf('minute').tz(timeZoneId);
    const end = moment(endTimestamp).startOf('minute').tz(timeZoneId);
    const diffDays = end.diff(start, 'days');
    const diffHrs = end.diff(start, 'hours') - diffDays * 24;
    const diffMins = end.diff(start, 'minutes') - diffDays * 24 * 60;
    const durationInDays = diffDays > 0
        ? formatDurationInDaysWithIntl(diffDays, locale) ||
            moment.duration(getDurationInDays(start, end), 'days').locale(locale).humanize()
        : '';
    const duration = diffHrs > 0 || diffMins > 0
        ? formatDurationWithIntl({ diffHrs, diffMins, locale }) ||
            moment.duration(getDurationInMinutes(start, end), 'minutes').locale(locale).humanize()
        : '';
    return `${durationInDays}${durationInDays ? ' ' : ''}${duration}`;
};
const formatDurationWithIntl = ({ diffHrs, diffMins, locale }) => {
    const showHours = diffHrs > 0;
    const diffValue = showHours ? diffHrs : diffMins;
    const minutesLeft = diffMins - diffHrs * 60;
    const shouldShowMinutesWithHours = showHours && minutesLeft !== 0;
    try {
        const numberFormat = new Intl.NumberFormat(locale, {
            style: 'unit',
            unit: showHours ? 'hour' : 'minute',
            unitDisplay: 'long',
        });
        const minuteFormatter = shouldShowMinutesWithHours
            ? new Intl.NumberFormat(locale, {
                style: 'unit',
                unit: 'minute',
                unitDisplay: 'long',
            })
            : null;
        return `${numberFormat.format(diffValue)} ${minuteFormatter ? minuteFormatter.format(minutesLeft) : ''}`.trim();
    }
    catch {
        return null;
    }
};
const formatDurationInDaysWithIntl = (diffDays, locale) => {
    try {
        const daysFormat = new Intl.NumberFormat(locale, {
            style: 'unit', // safari@13 support only: "decimal", "percent", "currency"
            unit: 'day',
            unitDisplay: 'long',
        });
        return daysFormat.format(diffDays);
    }
    catch (e) {
        return diffDays?.toString() ?? '';
    }
};
export const getDateWithoutWeekday = (timestamp, timeZone, locale) => formatWithIntl({
    timeZone,
    year: 'numeric',
    month: 'short',
    day: '2-digit',
}, timestamp, locale);
export const relativeFormat = (timestamp, locale) => {
    const current = now();
    const date = getMomentDate(timestamp);
    const diffHrs = current.diff(date, 'hours');
    if (diffHrs < 24) {
        try {
            const diffMins = current.diff(date, 'minutes');
            const diffSecs = current.diff(date, 'seconds');
            const showHours = diffHrs > 0;
            const showMinutes = diffMins > 0;
            const val = showHours ? -diffHrs : showMinutes ? -diffMins : -diffSecs;
            const rtf = new Intl.RelativeTimeFormat(locale);
            return rtf.format(val, showHours ? 'hour' : showMinutes ? 'minutes' : 'seconds');
        }
        catch {
            return date.from(current);
        }
    }
    return null;
};
export const formatTimestampWithoutTimeZone = (timestamp, timeZoneId, locale) => {
    const current = now();
    const date = getMomentDate(timestamp);
    if (current.diff(date, 'hour') < 24) {
        return relativeFormat(timestamp, locale);
    }
    return getLongDate(timestamp, timeZoneId, false, locale);
};
